import { useEffect, useMemo, useState } from 'react';
import { Phone, PhoneOff, Loader2, SkipForward, Headphones } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { useCallStore, startCallPolling } from '../../stores/callStore';
import { useTelecallerHotkeys } from '../../hooks/useTelecallerHotkeys';
import { api } from '../../utils/api';
import { resolveRoleKey } from '../../pages/dashboard/roleLayouts';

/**
 * CallBar visibility (Apr 24/28, 2026):
 *
 * The floating call-bar is only relevant to users who actually dial. Showing
 * it to Admin/Owner/dept-heads/Production/etc. leaks a sales-only affordance
 * into views where it has no business — Sun Stellar's owner Karan reported
 * seeing "Next from queue" on his Admin dashboard.
 *
 * Gate: show ONLY for Sales Manager, Sales Executive (unified field+office
 * +telecaller — see roleLayouts.ts), and Telecaller (legacy role alias).
 *
 * Apr 28, 2026 — also honour the TopBar "View as" sessionStorage override.
 * When admin View-as'es Sales / Sales Exec / Telecaller, the CallBar must
 * appear too (so the admin can rehearse the sales rep's calling flow).
 *
 * Secondary gate: if the tenant has an explicit `enabledModules` list AND it
 * doesn't include `telecaller`, hide even for the above roles. A tenant with
 * no list (null/undefined = all modules) is treated as allowed.
 */
const CALLBAR_ROLES = new Set(['SALES_MANAGER', 'SALES_EXECUTIVE', 'TELECALLER']);
const CALLBAR_VIEW_OVERRIDES = new Set(['sales', 'sales_executive', 'telecaller']);
const VIEW_OVERRIDE_KEY = 'aura-view-override';

function readViewOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(VIEW_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

function fmtDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function sentimentDot(sentiment?: string | null): string | null {
  if (!sentiment) return null;
  const s = sentiment.toLowerCase();
  if (s.includes('positive')) return 'bg-green-500';
  if (s.includes('negative')) return 'bg-red-500';
  return 'bg-gray-400';
}

export function CallBar(): JSX.Element | null {
  const { user, tenant } = useAuthStore();
  const activeCall = useCallStore((s) => s.activeCall);
  const dialLoading = useCallStore((s) => s.dialLoading);
  const error = useCallStore((s) => s.error);
  const dial = useCallStore((s) => s.dial);
  const hangup = useCallStore((s) => s.hangup);
  const clearError = useCallStore((s) => s.clearError);

  // Role + module gate (see comment at top of file).
  const roleKey = useMemo(() => resolveRoleKey(user as never), [user]);
  const [viewOverride, setViewOverride] = useState<string | null>(() => readViewOverride());

  // Listen for storage changes (e.g. from another tab) AND poll once a tick
  // because sessionStorage doesn't fire `storage` events in the same tab.
  useEffect(() => {
    const onStorage = () => setViewOverride(readViewOverride());
    window.addEventListener('storage', onStorage);
    const interval = setInterval(() => {
      const cur = readViewOverride();
      setViewOverride((prev) => (prev === cur ? prev : cur));
    }, 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const enabledModules = tenant?.enabledModules;
  const moduleAllowed =
    !enabledModules ||
    !Array.isArray(enabledModules) ||
    enabledModules.length === 0 ||
    enabledModules.includes('telecaller');

  const roleAllowed = CALLBAR_ROLES.has(roleKey);
  const overrideAllowed = viewOverride !== null && CALLBAR_VIEW_OVERRIDES.has(viewOverride);
  const show = (roleAllowed || overrideAllowed) && moduleAllowed;

  // ── Start polling on mount (role-gated) ──
  useEffect(() => {
    if (!show) return;
    startCallPolling();
    // Do NOT stop polling on unmount — the bar lives in the shell, so mount == session
  }, [show]);

  // ── Live-duration ticker while call is in progress ──
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!activeCall || !activeCall.startedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeCall?.callId, activeCall?.startedAt]);

  const liveDuration = useMemo(() => {
    if (!activeCall?.startedAt) return activeCall?.duration || 0;
    const started = new Date(activeCall.startedAt).getTime();
    if (Number.isNaN(started)) return activeCall.duration || 0;
    return Math.max(0, Math.floor((Date.now() - started) / 1000));
  }, [activeCall, tick]);

  // ── Hotkeys ──
  useTelecallerHotkeys(
    [
      {
        key: 'Escape',
        handler: () => {
          if (activeCall) void hangup();
        },
      },
      {
        key: 'Space',
        handler: (e) => {
          // Reserve for accept — no-op for MVP. Guard against scroll.
          if (activeCall && (activeCall.status === 'RINGING' || activeCall.status === 'INCOMING')) {
            e.preventDefault();
            // TODO: wire accept when backend supports it
          }
        },
      },
    ],
    show,
  );

  // ── Dial input (only when no active call) ──
  const [manualPhone, setManualPhone] = useState('');
  const [nextLoading, setNextLoading] = useState(false);

  const onNextFromQueue = async () => {
    setNextLoading(true);
    try {
      const res = await api.get('/telecaller/queue', { params: { limit: 1 } });
      const lead = (res.data?.data || [])[0];
      if (!lead) {
        // Surface via the existing error pill (auto-clears in 5s) instead of a blocking alert.
        useCallStore.setState({ error: 'Your call queue is empty.' });
        return;
      }
      await dial(lead.id, lead.contact?.phone || lead.contact?.mobile);
    } catch {
      // dial() populates error
    } finally {
      setNextLoading(false);
    }
  };

  const onDialManual = async () => {
    if (!manualPhone.trim()) return;
    // Backend expects leadId — for manual dial, send the phone number only.
    // The backend must accept an ad-hoc phoneNumber (no leadId) for manual dial.
    // Pass empty leadId — let backend validate.
    try {
      await dial('', manualPhone.trim());
      setManualPhone('');
    } catch {
      // handled by store
    }
  };

  // ── Error auto-clear ──
  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => clearError(), 5000);
    return () => clearTimeout(id);
  }, [error, clearError]);

  if (!show) return null;

  const agentName = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : '';
  const sDot = sentimentDot(activeCall?.sentiment);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 h-16 border-t border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-lg">
      <div className="flex h-full items-center gap-4 px-4">
        {/* Left — brand + agent */}
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Headphones size={16} className="text-[var(--ink-0)]" />
          <span className="font-medium text-[var(--text-primary)]">{agentName}</span>
          {!activeCall && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Ready
            </span>
          )}
        </div>

        {/* Middle — either dial input OR active call */}
        {!activeCall ? (
          <div className="flex flex-1 items-center gap-2">
            <input
              type="tel"
              inputMode="tel"
              aria-label="Phone number to dial"
              autoComplete="tel"
              placeholder="+91 98XXX XXXXX"
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onDialManual();
              }}
              className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus-visible:border-[var(--ink-0)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ink-0)]"
            />
            <button
              onClick={onDialManual}
              disabled={dialLoading || !manualPhone.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {dialLoading ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
              Dial
            </button>
            <button
              onClick={onNextFromQueue}
              disabled={nextLoading || dialLoading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {nextLoading ? <Loader2 size={14} className="animate-spin" /> : <SkipForward size={14} />}
              Next from queue
            </button>
            {error && (
              <span className="ml-2 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
                {error}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
              </span>
              <span className="text-sm font-semibold text-[var(--text-primary)]">
                {activeCall.leadName || 'Unknown'}
              </span>
              <span className="text-xs text-[var(--text-muted)]">{activeCall.phoneNumber}</span>
            </div>
            <div className="rounded-md bg-gray-100 px-2 py-1 font-mono text-sm font-semibold text-gray-800 tabular-nums">
              {fmtDuration(liveDuration)}
            </div>
            {sDot && (
              <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                <span className={`h-2 w-2 rounded-full ${sDot}`} />
                {activeCall.sentiment}
              </span>
            )}
            <span className="text-[11px] text-[var(--text-muted)]">
              Status: <strong>{activeCall.status}</strong>
            </span>
            <span className="ml-auto hidden text-[11px] text-[var(--text-muted)] sm:inline">
              Press <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono text-[10px]">Esc</kbd> to hang up
            </span>
          </div>
        )}

        {/* Right — hang-up */}
        {activeCall && (
          <button
            onClick={() => hangup()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            <PhoneOff size={14} /> Hang up
          </button>
        )}
      </div>
    </div>
  );
}
